/**
 * How the text names one of the book's images (decision 37; part 16a log, table N): `![alt](image:<id>?sha256=<hash>)`,
 * the image's random 128-bit ID and the SHA-256 of its stored bytes, both lowercase hex. The hash travels with every
 * copy of the text (snapshots, conflict copies, trash, bundles), so the image is checked wherever the text goes.
 */
import { serializeChapter } from '../literal/serialize.ts';

/** An image as every reference names it: its ID (32 lowercase hex digits) and its bytes' SHA-256 (64). */
export interface ImageReference {
  readonly id: string;
  readonly sha256: string;
}

const DESTINATION = /^image:[0-9a-f]{32}\?sha256=[0-9a-f]{64}$/u;
/** Where the ID and the hash stand in a destination: after `image:`, and after the ID and `?sha256=`. */
const ID_AT = 6;
const HASH_AT = ID_AT + 32 + 8;

/** N1, N2: the image a link destination names, or null when it isn't exactly a reference. */
export function readImageDestination(destination: string): ImageReference | null {
  if (!DESTINATION.test(destination)) return null;
  return { id: destination.slice(ID_AT, HASH_AT - 8), sha256: destination.slice(HASH_AT) };
}

/** N3: the destination naming `reference`; throws a `TypeError` for an ID or hash that wouldn't read back. */
export function imageDestination(reference: ImageReference): string {
  const destination = `image:${reference.id}?sha256=${reference.sha256}`;
  if (!DESTINATION.test(destination)) throw new TypeError('an image reference needs a 32-digit ID and a 64-digit hash in lowercase hex');
  return destination;
}

/** `bytes` as lowercase hex, two digits a byte. */
export function toHex(bytes: Uint8Array): string {
  let hex = '';
  for (const byte of bytes) hex += byte.toString(16).padStart(2, '0');
  return hex;
}

/** Part 16b, I6: the most characters (code points) an alt text an image is put in with keeps. */
export const MAX_ALT_LENGTH = 100;

/**
 * Part 16b, I6: `text` as an image's alt text: every run of white space and control characters one space (an alt is one
 * line of text), trimmed, cut to {@link MAX_ALT_LENGTH} code points (never half a surrogate pair), trimmed again; empty
 * when nothing readable is left.
 */
export function cleanAlt(text: string): string {
  const one = text.replaceAll(/[\s\p{Cc}]+/gu, ' ').trim();
  return Array.from(one).slice(0, MAX_ALT_LENGTH).join('').trim();
}

/**
 * Part 16b, I6, principle 1: the stored markup of an image put in the text, `![alt](image:…)`, canonical, its alt
 * {@link cleanAlt}'s and all of it literal text. Throws a `TypeError` for an alt with nothing readable (an image with
 * none shows nothing in the text, part 09's D7) and for a reference {@link imageDestination} refuses.
 */
export function imageMarkup(alt: string, reference: ImageReference): string {
  const text = cleanAlt(alt);
  if (text === '') throw new TypeError('an image needs an alt text');
  const children = [{ kind: 'text' as const, pieces: [{ text, from: 0, to: text.length, origin: 'literal' as const }] }];
  const image = { kind: 'image' as const, children, destination: imageDestination(reference), title: null, from: 0, to: 0, textEnd: 0 };
  // The serialiser writes a paragraph of the image alone, ended by its line's newline.
  return serializeChapter({ blocks: [{ kind: 'paragraph', content: [image], from: 0, to: 0 }] }).slice(0, -1);
}
